<template>
    <component
        :is="forHistoryPage ? 'div' : TheModalTitle"
        container-class=""
        :model-value="modelValue"
        :backdrop="backdrop"
        @update:model-value="onModelValue"
    >
        <template #header v-if="!forHistoryPage">
            <span class="text-[24px] leading-10">
                {{ `${$t('History')}` }}
            </span>
        </template>
        <div class="w-full">
            <div class="my-1 text-[#93acd3]">{{$t('game.statisticsTitle')}}</div>
            <div class="figure-box pb-[10px]">
                <div class="left-user">
                    <UserAvatar class="shrink-0" size="42px" self />
                    <span class="text truncate">
                        {{ userStore.name }}
                    </span>
                </div>
                <div class="right-amount">
                    <CurrentCoin class="" size="24px" />
                    <span class="text">
                        {{ getFixed(winAmountSum) }}
                    </span>
                </div>
            </div>
            <div class="big-list gray-box">
                <q-table
                    key="collecting"
                    v-bind="oTableSetting"
                    row-key="name"
                    :no-data-label="$t('Noinformation')"
                    :rows="oTableData.tableArr"
                    :columns="oTableData.aColumns"
                    table-class=""
                    :class="{
                        [forHistoryPage ? '' : 'h-[300px]']: true,
                    }"
                >
                    <template v-slot:body="props">
                        <q-tr :props="props">
                            <q-td class="td-time" key="timestamp">
                                <div class="td-inner">
                                    <span class="text">
                                        {{ fnGetTime(props.row.timestamp * 1000)[0] }}<br />
                                        {{ fnGetTime(props.row.timestamp * 1000)[1] }}
                                    </span>
                                </div>
                            </q-td>
                            <q-td class="td-room" key="name">
                                {{ locale === 'cn' ? props.row.hallNameCn : props.row.hallName }}
                            </q-td>
                            <q-td class="td-amount" key="amount">
                                <div class="td-inner">
                                    <CurrentCoin size="18px" class="" :name="props.row.currency" />
                                    <span class="flex flex-col">
                                        <span
                                            class="text"
                                            :class="{ won: props.row.amount > 0 }"
                                        >
                                            {{ getFixed(props.row.amount) }}
                                        </span>
                                    </span>
                                </div>
                            </q-td>
                            <q-td class="td-amount" key="diff">
                                <div class="td-inner">
                                    <CurrentCoin size="18px" class="" :name="props.row.currency" />
                                    <span class="flex flex-col">
                                        <span
                                            class="text"
                                            :class="{ won: props.row.diff > 0 }"
                                        >
                                            {{ Number(props.row.diff).toFixed(2) }}
                                        </span>
                                    </span>
                                </div>
                            </q-td>
                            <q-td class="td-btn" key="amountInUsd">
                                <button class="see-deail" @click.prevent="fnSeeDetail(props.row)">
                                    <IconSvgUse use="icon-info-circle-2" size="16px" />
                                </button>
                            </q-td>
                        </q-tr>
                    </template>
                    <template v-slot:no-data="{ message }">
                        <div class="loading-box" v-if="oTableData.isLoading">
                            <IconSvgUse use="icon-loading" size="30px" class="animate-spin" />
                        </div>
                        <div class="no-data-message" v-else>
                            <img class="no-data-pic" src="@/assets/imgs/no-data.webp" />
                            <p class="no-data-tips">
                                {{ message }}
                            </p>
                        </div>
                    </template>
                </q-table>
                <div class="history-pagination" v-if="forHistoryPage">
                    <q-pagination
                        v-model="oTableData.pageConfig.page"
                        :max="Math.ceil(oTableData.total / oTableData.pageConfig.pageSize)"
                        :max-pages="4"
                        boundary-numbers
                        color="[#55657e]"
                        active-color="[#2281f6]"
                        direction-links
                        @update:model-value="(iPageNo) => fnTableData(iPageNo)"
                    />
                </div>
            </div>
            <!-- ↓ 最后的提示文字 -->
            <p class="bottom-tips" v-if="!forHistoryPage">
                {{
                    $t('goldenFlower.AsMuch20Records', {
                        rows: oRowOriginalConfig.pageSize,
                        day: 7,
                    })
                }}
            </p>
        </div>
    </component>
    <!-- 
        ↓ 以下是窗口 
    -->
    <ClientOnly>
        <!-- ↓ 浮层页专用，另外在游戏内部时是通过修改 route 参数触发弹出游戏详情，不依赖此处的组件 -->
        <HistoryDetail
            v-if="forHistoryPage"
            gameDropValue="goldenFlower"
            :historyDetail="oHistoryQuery"
        />
    </ClientOnly>
</template>

<script setup lang="ts">
import moment from 'moment';
import TheModalTitle from '@/components/Modal/Title.vue';
import HistoryDetail from '@/components/Casino/History/Modal/GameDetali/index.vue';
import { useUserStore } from '@/stores/user';
import { useGoldenFlower } from '@/socket/game/goldenFlower';

const props = defineProps({
    modelValue: Boolean, //
    forHistoryPage: {
        type: Boolean,
        default: false,
    },
    backdrop: {
        type: Boolean,
        default: true,
    },
});
const emit = defineEmits(['update:modelValue']);
const { EMIT_ZHAJINHUA_HISTORY } = useGoldenFlower();
const oRowOriginalConfig = {
    page: 1,
    pageSize: 20, // 固定 50
};
const oTableSetting = {
    'pagination': {
        rowsPerPage: 20, // 接口最多返回 20 行
    },
    'hide-pagination': !!1,
    'class': 'big-table',
};
const userStore = useUserStore();
const { t: $tFn, locale } = useNuxtApp().$i18n;
const oHistoryQuery = ref({});
const oTableData = reactive({
    aColumns: [
        {
            label: $tFn('Time'),
            name: '',
            field: '',
        },
        {
            label: $tFn('goldenFlower.room'),
            name: '', // 添加 name, field 消除 ts 报错
            field: '',
        },
        {
            label: $tFn('BetAmount'),
            name: '',
            field: '',
        },
        {
            label: $tFn('goldenFlower.win'),
            name: '',
            field: '',
        },
        {
            label: '',
            name: '',
            field: '',
        },
    ],
    tableArr: [],
    total: 0,
    isLoading: false,
    pageConfig: deepCopy(oRowOriginalConfig),
});

const winAmountSum= ref(0);

/**
 * 控制自身的开/关
 */
const onModelValue = (value) => {
    // console.log('收到新值：', value);
    emit('update:modelValue', !!value);
};

const fnGetTime = (ts: number) => {
    const sFormst = locale.value === 'cn' ? 'YYYY/MM/DD HH:mm:ss' : 'MM/DD/YYYY hh:mm:ss A';
    const sTime = moment(ts).format(sFormst);
    const aTime = sTime.split(' ');
    return [aTime[0], aTime.slice(1).join(' ')];
};

/**
 * 加载当前页的数据
 */
const fnTableData = async (iPageNo = null) => {
    console.log('iPageNo', iPageNo); // 调试
    // ↓ 在顶部提前声明出来
    const oTarget = oTableData;
    Object.assign(oTarget, {
        // tableArr: [], // 是否清空，观察中
        isLoading: true,
    });
    const res = await EMIT_ZHAJINHUA_HISTORY({
        userId: userStore._id,
        page: oTarget.pageConfig.page,
        pageSize: oTarget.pageConfig.pageSize,
    }).finally(()=>{
        oTarget.isLoading = false;
    });
    if (!res || res['error']){
        showNegativeNotify(res?.['error']);
        return;
    }
    console.log('res',res);
    winAmountSum.value = res['userBets'].reduce((sum, item: any) => sum + Number(item.diff), 0);
    Object.assign(oTarget, {
        total: res['total'] || 0,
        tableArr: res['userBets'] || [],
    });
    console.log('history ↓\n', deepCopy(oTarget));
};

/**
 * 查看历史的详情
 */
const fnSeeDetail = (oTarget) => {
    console.log('炸金花游戏查看历史详情，打开窗口', oTarget);
    const route = useRoute();
    const query = {
        modal: 'BetDetails',
        data: `zhajinhua:${oTarget.orderId}`,
        gamePlayId: oTarget.gamePlayId,
        name: route.query.name,
        ...(route.query.src ? { src: route.query.src } : {}),
    };
    if (props.forHistoryPage) {
        oHistoryQuery.value = query;
        return;
    }

    const router = useRouter();
    router.push({
        path: route.path,
        query,
    })
    // router.replace({
    //     path: route.path,
    //     query,
    // });
};

// ↓监听窗口自身的打开 
watch(() => props.modelValue, async (isShowing) => {
    if (
        import.meta.server
        || !isShowing
        || props.forHistoryPage
    ) {
        return;
    }
    fnTableData();
}, {
    immediate: true,
});

onMounted(() => {
    if (props.forHistoryPage) {
        fnTableData();
    }
});
</script>

<style lang="scss" scoped>
.figure-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    // margin: 15px 0 15px;
    .left-user,
    .right-amount {
        display: flex;
        align-items: center;
        & > *:nth-child(1) {
            margin-right: 8px;
        }
    }
    .left-user {
        margin: 0 20px 0 0;
        overflow: hidden;
        .text {
            font-weight: 600;
            font-size: 14px;
            color: rgba(167, 181, 202, 1);
        }
    }
    .right-amount {
        .text {
            font-weight: 700;
            font-size: 24px;
            text-align: right;
            color: white;
        }
    }
}

.gray-box {
    background: rgba(32, 42, 57, 1);
    padding: 12px;
    border-radius: 8px;
}

.analysis {
    margin: 10px 0;
    padding: 12px 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    & > * {
        flex: 1 1 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }
    .title {
        font-weight: 500;
        font-size: 12px;
        line-height: 12px;
        color: rgba(147, 172, 211, 1);
        margin: 0 0 12px;
    }
    .quantity {
        color: white;
        font-weight: 800;
        font-size: 20px;
        display: flex;
        align-items: center;
        img {
            width: 22px;
            margin: 0 6px 0 -3px;
        }
    }
}

.big-list {
    // .big-table{
    //     height: 300px;
    // }
    & :deep(.q-table__card) {
        background-color: transparent;
        box-shadow: none;
    }
    & :deep(table.q-table) {
        width: 100%;
        overflow: hidden;
        background-color: transparent;
        tr {
            // border-bottom: 0.5px solid var(--bet-corgi-color-white-12, rgba(255, 255, 255, 0.12))
            border-bottom: solid 1px rgba(255, 255, 255, 0.15);
        }
        tbody tr:hover {
            background-color: rgb(0, 0, 0, 0.1);
        }
        th {
            color: rgba(85, 101, 126, 1);
            font-size: 12px;
            text-align: left;
            padding: 0 3px;
        }
        td {
            opacity: 1;
            padding: 0 3px;
        }
    }
    td .td-inner {
        font-size: 14px;
        line-height: 14px;
        vertical-align: middle;
        display: flex;
        align-items: center;
        overflow: hidden;
        max-width: 99%;
        // padding: 8px 0;
        min-height: 48px;
        .user-avatar,
        img {
            margin: 0 5px 0 0;
        }
        .text {
            min-width: 10px;
        }
    }
    .td-room {
        color: white;
        font-weight: 500;
        font-size: 14px;
        min-width: 65px;
    }
    .td-amount {
        color: rgba(167, 181, 202, 1);
        min-width: 65px;
        font-weight: 700;
        img {
            display: inline-block;
        }
        .won {
            color: rgba(27, 184, 61, 1);
        }
    }
    .td-time {
        .text {
            color: rgba(167, 181, 202, 1);
            font-weight: 600;
            font-size: 12px;
        }
    }
    .td-detail {
        color: white;
        font-weight: 700;
        .small-pack-icon {
            display: none;
        }
        em {
            color: rgba(27, 184, 61, 1);
        }
    }
    .td-btn {
        button.see-deail {
            // margin: 0 10px 0 auto;
            background: rgba(68, 88, 116, 0.24);
            color: rgba(151, 151, 151, 1);
            width: 20px;
            height: 20px;
            border-radius: 5px;
            display: flex;
            justify-content: center;
            align-items: center;
            &:hover,
            &:active {
                opacity: 0.9;
            }
            .q-icon {
                margin: 0;
                display: inline-block;
                opacity: 0.7;
            }
        }
    }
}

.history-pagination {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 20px;
    margin-top: 20px;
    :deep(.q-pagination) {
        button {
            background: #545c6c40;
            color: #55657e;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 600;
            height: 35px;
            min-width: 35px !important;
            margin: 0 2px;
        }
        .q-btn--standard {
            background: #2281f6;
            font-size: 15px;
        }
    }
}

@media screen and (min-width: 361px) {
    .small-pack-icon {
        display: inline-block !important;
    }
}

.bottom-tips {
    text-align: center;
    color: rgba(167, 181, 202, 1);
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    vertical-align: middle;
    padding: 0.8em 0 0;
}

.loading-box {
    min-height: 150px;
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 45px 0 0;
}

.no-data-message {
    min-height: 150px;
    width: 100%;
    padding: 20px;
    text-align: center;
    .no-data-pic {
        width: 165px;
        margin: 0 auto 0;
    }
    .no-data-tips {
        font-size: 14px;
        color: rgba(167, 181, 202, 1);
    }
}
</style>
